/** Build rows for the Progress Report (MASTER-style deal list). */

import {
  resolveProgressDateRaw,
  toCalendarIsoDay,
} from "./tradeDashboardDates";
import { complianceReportFromTrackerFields } from "../constants/complianceChecklist";

/** Columns edited with a dropdown in the progress report modal. */
export const PROGRESS_REPORT_SELECT_OPTIONS = {
  status: [
    { value: "AVAILABLE", label: "Available" },
    { value: "SOLD", label: "Sold" },
    { value: "CLOSED", label: "Closed" },
    { value: "LEASED", label: "Leased" },
    { value: "EXPIRED", label: "Expired" },
    { value: "PENDING", label: "Pending" },
    { value: "READY", label: "Ready" },
    { value: "FALLEN THRU", label: "Fallen Thru" },
    { value: "DUPLICATE", label: "Duplicate" },
  ],
  tradeCategory: [
    { value: "Pre-Construction", label: "Pre-Construction" },
    { value: "Resale", label: "Resale" },
    { value: "Lease", label: "Lease" },
  ],
  compliance: [
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
    { value: "Pending", label: "Pending" },
  ],
};

/** One canonical stored value per status (avoids duplicate dropdown entries). */
export function canonicalTradeStatus(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "AVAILABLE";
  const upper = raw.toUpperCase();
  for (const opt of PROGRESS_REPORT_SELECT_OPTIONS.status) {
    if (opt.value === upper || opt.value === raw) return opt.value;
    if (opt.label.toUpperCase() === upper) return opt.value;
  }
  return upper;
}

function mergeProgressTracker(trade) {
  const pt = trade?.progressTracker || {};
  const k = trade?.keyInfo || {};
  return {
    ...pt,
    paymentReceivedDate:
      pt.paymentReceivedDate || k.paymentReceivedDate || "",
    paymentConfirmationSentDate:
      pt.paymentConfirmationSentDate || k.paymentConfirmationSentDate || "",
    closedTRSDate: pt.closedTRSDate || k.closedTRSDate || "",
    fileCompleteDate: pt.fileCompleteDate || k.fileCompleteDate || "",
    invoicedDate: pt.invoicedDate || k.invoicedDate || "",
    commissionInvoiceReceivedDate:
      pt.commissionInvoiceReceivedDate || k.commissionInvoiceReceivedDate || "",
  };
}

export function getTradePartNumber(trade) {
  const tn = trade?.tradeNumber ?? trade?.keyInfo?.tradeNumber ?? "";
  return String(tn).trim();
}

/** Base trade # without -A / -B suffix (e.g. 200-A → 200). */
export function getBaseTradeNumber(trade) {
  const part = getTradePartNumber(trade);
  const m = part.match(/^(\d+)/);
  return m ? m[1] : part;
}

function formatReportDate(value) {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const iso = toCalendarIsoDay(raw);
  if (iso) {
    const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      return new Date(y, m - 1, d).toLocaleDateString("en-CA");
    }
  }
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString("en-CA");
  }
  return raw;
}

/** Commission invoice sent — date (Progress Tracker: invoicedDate). */
function formatInvoiceSentDate(trade) {
  const pt = mergeProgressTracker(trade);
  const raw = pt.invoicedDate || "";
  return raw ? formatReportDate(raw) : "";
}

function isCoopTradeClass(tradeClass) {
  return String(tradeClass || "").trim().toUpperCase() === "CO-OPERATING SIDE";
}

/**
 * One invoice column: listing-side = commission invoice sent (invoicedDate);
 * co-operating = commission invoice received date.
 */
export function formatProgressReportInvoiceDate(trade) {
  const k = trade?.keyInfo || {};
  const pt = mergeProgressTracker(trade);
  if (isCoopTradeClass(k.classification)) {
    const raw = pt.commissionInvoiceReceivedDate || pt.invoicedDate || "";
    return raw ? formatReportDate(raw) : "";
  }
  return formatInvoiceSentDate(trade);
}

function formatPrice(value) {
  const n = parseFloat(String(value || "").replace(/[^0-9.-]/g, ""));
  if (Number.isNaN(n) || n === 0) return "";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function formatMoney(value) {
  const n = parseFloat(String(value || "").replace(/[^0-9.-]/g, ""));
  if (Number.isNaN(n)) return "";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatAddress(k) {
  const parts = [k.streetNumber, k.streetName, k.unit].filter(
    (p) => p != null && String(p).trim() !== "",
  );
  return parts.join(" ").trim();
}

function commissionIncomeTotal(trade) {
  const rows = trade?.commission?.commissionIncomeRows || [];
  return rows.reduce((sum, row) => sum + (parseFloat(row.total) || 0), 0);
}

function computeARAmount(trade) {
  const k = trade?.keyInfo || {};
  const classification = k.classification || "";
  const total = commissionIncomeTotal(trade);
  const trustRecords = trade?.trustRecords || [];
  const trustSum = trustRecords.reduce(
    (sum, r) => sum + (parseFloat(r.amount) || 0),
    0,
  );

  if (
    classification === "LISTING SIDE" ||
    classification === "AGENT DOUBLE ENDER"
  ) {
    const weHoldYes = trustRecords.some((r) => r.weHold === "Yes");
    if (weHoldYes) return formatMoney(trustSum - total);
    return formatMoney(total);
  }
  if (classification === "CO-OPERATING SIDE") {
    return formatMoney(total);
  }
  return "";
}

function getOutsideBrokersSummary(trade) {
  const rows = trade?.commission?.outsideBrokersRows || [];
  if (!rows.length) return { outsideBroker: "", invoiceAmount: "" };
  const names = [
    ...new Set(
      rows
        .map((r) => String(r.brokerage || r.agentName || "").trim())
        .filter(Boolean),
    ),
  ];
  const invoiceTotal = rows.reduce(
    (sum, r) => sum + (parseFloat(r.total) || 0),
    0,
  );
  return {
    outsideBroker: names.join(", "),
    invoiceAmount: invoiceTotal ? formatMoney(invoiceTotal) : "",
  };
}

/** Report compliance dropdown; maps progress tracker + checklist. */
export function mapComplianceForReport(pt) {
  return complianceReportFromTrackerFields(pt || {});
}

function getAgentNames(trade) {
  const names = [];
  for (const a of trade?.agentCommissionList || []) {
    const cls = (a.classification || "").toUpperCase();
    if (cls === "REFERRAL") continue;
    const name =
      a.agentName ||
      [a.firstName, a.lastName].filter(Boolean).join(" ").trim();
    if (name && !names.includes(name)) names.push(name);
  }
  return names.join(", ");
}

export function hasProgressTrackerInvoiceYes(pt) {
  if (!pt) return false;
  return (
    pt.commissionInvoiceReceived === "Yes" ||
    pt.commissionInvoiceSentListing === "Yes" ||
    pt.commissionInvoiceSentCoop === "Yes"
  );
}

export function formatInvoicedStatus(trade) {
  const pt = mergeProgressTracker(trade);
  if (pt.commissionInvoiceReceived === "Requested") {
    return "Requested";
  }
  if (hasProgressTrackerInvoiceYes(pt) || pt.invoicedDate) {
    const raw = pt.invoicedDate || pt.fileCompleteDate || "";
    return raw ? formatReportDate(raw) : "";
  }
  return "";
}

function paymentReceivedDisplayDate(trade) {
  const iso = String(trade?.paymentReceivedEftDateIso || "").trim();
  return iso ? formatReportDate(iso) : "";
}

function paymentToAgentsDisplayDate(trade) {
  const agentIso = String(trade?.paymentToAgentsEftDateIso || "").trim();
  if (agentIso) return formatReportDate(agentIso);
  const obIso = String(trade?.paymentToOutsideBrokerEftDateIso || "").trim();
  if (obIso) return formatReportDate(obIso);
  return "";
}

/** Listing-side trades (not co-operating). */
export function isListingSideTradeClass(tradeClass) {
  return (
    String(tradeClass || "").trim().toUpperCase() !== "CO-OPERATING SIDE"
  );
}

/** Listing-side invoice sent = blue; co-op invoice received = default white. */
export function getProgressReportInvoiceDateCellClass(columnKey, row) {
  if (columnKey === "invoiceDate" && row?.isListingSide) {
    return "bg-blue-50 text-blue-800 font-medium";
  }
  return "";
}

function formatOpenTRSDate(trade) {
  const pt = mergeProgressTracker(trade);
  const raw = pt.openTRSDate || "";
  return raw ? formatReportDate(raw) : "";
}

function formatClosedTRSDate(trade) {
  const pt = mergeProgressTracker(trade);
  const raw = pt.closedTRSDate || "";
  return raw ? formatReportDate(raw) : "";
}

export function progressReportTradeLabel(trade) {
  const tn = trade?.tradeNumber ?? trade?.keyInfo?.tradeNumber ?? "";
  const sn = trade?.keyInfo?.streetNumber || "";
  const st = trade?.keyInfo?.streetName || "";
  const addr = [sn, st].filter(Boolean).join(" ");
  return addr ? `#${tn} — ${addr}` : `#${tn}`;
}

/** Searchable trade filter options (descending trade #), same labels as Progress Tracker. */
export function buildProgressReportTradeFilterOptions(trades) {
  return (trades || [])
    .map((t) => {
      const value = String(
        t.tradeNumber ?? t.keyInfo?.tradeNumber ?? "",
      ).trim();
      if (!value) return null;
      return { value, label: progressReportTradeLabel(t) };
    })
    .filter(Boolean)
    .sort((a, b) => compareBaseTradeNumberDesc(a.value, b.value));
}

/** Trade picker options keyed by Mongo _id (for API / detail views). */
export function buildProgressReportTradePickOptions(trades) {
  return (trades || [])
    .map((t) => {
      const id = t._id != null ? String(t._id) : "";
      const tradeNum = String(
        t.tradeNumber ?? t.keyInfo?.tradeNumber ?? "",
      ).trim();
      if (!id) return null;
      return {
        value: id,
        label: progressReportTradeLabel(t),
        tradeNum,
      };
    })
    .filter(Boolean)
    .sort((a, b) => compareBaseTradeNumberDesc(a.tradeNum, b.tradeNum));
}

export function buildProgressReportRow(trade) {
  const k = trade?.keyInfo || {};
  const pt = mergeProgressTracker(trade);
  const tradeNumber = getTradePartNumber(trade);
  const id = trade._id != null ? String(trade._id) : "";
  const ob = getOutsideBrokersSummary(trade);
  return {
    rowKey: id || tradeNumber,
    tradeId: id,
    tradeNumber,
    baseTradeNumber: getBaseTradeNumber(trade),
    address: formatAddress(k),
    closingDate: formatReportDate(k.closeDate),
    status: canonicalTradeStatus(k.status),
    tradeClass: k.classification || "",
    isListingSide: isListingSideTradeClass(k.classification),
    tradeCategory: String(pt.tradeCategory || "").trim(),
    agentName: getAgentNames(trade) || "—",
    compliance: mapComplianceForReport(pt),
    invoiceDate: formatProgressReportInvoiceDate(trade),
    openTRSDate: formatOpenTRSDate(trade),
    closedTRSDate: formatClosedTRSDate(trade),
    price: formatPrice(k.sellPrice),
    priceRaw: parseFloat(String(k.sellPrice || "").replace(/[^0-9.-]/g, "")) || 0,
    arAmount: computeARAmount(trade),
    invoiceAmount: ob.invoiceAmount,
    outsideBroker: ob.outsideBroker,
    paymentReceivedDate: paymentReceivedDisplayDate(trade),
    paymentToAgentsDate: paymentToAgentsDisplayDate(trade),
    action: pt.action != null ? String(pt.action).trim() : "",
    followUpDate: formatReportDate(pt.followUpDate),
    notes: pt.notes != null ? String(pt.notes).trim() : "",
  };
}

export function buildProgressReportRows(trades) {
  return (trades || [])
    .map((trade) => buildProgressReportRow(trade))
    .sort((a, b) => {
      const na = parseInt(String(a.baseTradeNumber).replace(/\D/g, ""), 10) || 0;
      const nb = parseInt(String(b.baseTradeNumber).replace(/\D/g, ""), 10) || 0;
      if (nb !== na) return nb - na;
      return String(a.baseTradeNumber).localeCompare(String(b.baseTradeNumber));
    });
}

export function cloneProgressReportRows(rows) {
  return (rows || []).map((row) => ({ ...row }));
}

/** Merge preset options with any values already present in report rows. */
export function getProgressReportSelectOptions(columnKey, rows) {
  const base = PROGRESS_REPORT_SELECT_OPTIONS[columnKey];
  if (!base) return null;
  const labelByValue = new Map(base.map((o) => [o.value, o.label]));
  const seen = new Set();
  const merged = [];

  const add = (rawValue) => {
    const v = String(rawValue ?? "").trim();
    if (!v) return;
    const value =
      columnKey === "status" ? canonicalTradeStatus(v) : v;
    if (seen.has(value)) return;
    seen.add(value);
    merged.push({
      value,
      label: labelByValue.get(value) || v,
    });
  };

  for (const o of base) add(o.value);
  for (const row of rows || []) {
    add(row[columnKey]);
  }
  return merged;
}

export function isProgressReportSelectColumn(columnKey) {
  return Boolean(PROGRESS_REPORT_SELECT_OPTIONS[columnKey]);
}

export const PROGRESS_REPORT_COLUMNS = [
  { key: "tradeCategory", label: "Trade Category", edit: "select" },
  { key: "tradeNumber", label: "Trade #" },
  { key: "address", label: "Address" },
  { key: "closingDate", label: "Closing Date" },
  { key: "status", label: "Status" },
  { key: "tradeClass", label: "Trade Class" },
  { key: "agentName", label: "Agent" },
  { key: "compliance", label: "Compliance", edit: "select" },
  { key: "invoiceDate", label: "Invoice Date" },
  { key: "openTRSDate", label: "Open TRS Date" },
  { key: "closedTRSDate", label: "Closed TRS Date" },
  { key: "price", label: "Price" },
  { key: "arAmount", label: "AR Amount" },
  { key: "invoiceAmount", label: "Invoice Amount" },
  { key: "outsideBroker", label: "Outside Broker" },
  { key: "paymentReceivedDate", label: "Commission Received" },
  { key: "paymentToAgentsDate", label: "Commission paid out" },
  { key: "followUpDate", label: "Follow Up Date", edit: "date" },
  { key: "action", label: "Action" },
  { key: "notes", label: "Notes" },
];

/** Optional fixed widths for on-screen report table (modal). */
export const PROGRESS_REPORT_COLUMN_CELL_CLASS = {
  address: "min-w-[11rem] w-[11rem] max-w-[11rem]",
  agentName: "min-w-[10rem] w-[10rem] max-w-[10rem]",
  tradeClass: "min-w-[9rem] w-[9rem] max-w-[9rem]",
  outsideBroker: "min-w-[10rem] w-[10rem] max-w-[10rem]",
  action: "min-w-[12rem] w-[12rem] max-w-[12rem]",
  notes: "min-w-[16rem] w-[16rem] max-w-[16rem]",
};

export function getProgressReportColumnCellClass(columnKey) {
  return PROGRESS_REPORT_COLUMN_CELL_CLASS[columnKey] || "";
}

/** Columns with filter dropdowns above the table (empty = all). */
export const PROGRESS_REPORT_FILTER_KEYS = [
  "tradeCategory",
  "tradeNumber",
  "address",
  "closingDate",
  "status",
  "tradeClass",
  "agentName",
  "compliance",
  "invoiceDate",
  "openTRSDate",
  "closedTRSDate",
  "price",
  "arAmount",
  "invoiceAmount",
  "outsideBroker",
  "paymentReceivedDate",
  "paymentToAgentsDate",
  "action",
  "followUpDate",
  "notes",
];

export function getProgressReportFilterOptions(columnKey, rows) {
  const values = new Set();
  for (const row of rows || []) {
    const v = String(row[columnKey] ?? "").trim();
    values.add(v || "—");
  }
  const sorted = [...values].sort((a, b) => {
    if (a === "—") return 1;
    if (b === "—") return -1;
    return a.localeCompare(b, undefined, { sensitivity: "base" });
  });
  return [{ value: "", label: "All" }, ...sorted.map((v) => ({ value: v, label: v }))];
}

/** Filter value is a typed partial match (e.g. trade # contains query). */
export const PROGRESS_REPORT_TYPEABLE_FILTER_KEYS = new Set(["tradeNumber"]);

/** Address filter: partial match (contains) on typed or selected value. */
export const PROGRESS_REPORT_PARTIAL_MATCH_FILTER_KEYS = new Set(["address"]);

/** Unique addresses for filter dropdown (non-empty, sorted). */
export function buildProgressReportAddressFilterOptions(rows) {
  const seen = new Set();
  const list = [];
  for (const row of rows || []) {
    const v = String(row.address ?? "").trim();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    list.push({ value: v, label: v });
  }
  list.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
  );
  return list;
}

export function compareBaseTradeNumberDesc(a, b) {
  const na = parseInt(String(a).replace(/\D/g, ""), 10) || 0;
  const nb = parseInt(String(b).replace(/\D/g, ""), 10) || 0;
  if (nb !== na) return nb - na;
  return String(b).localeCompare(String(a));
}

/** Unique trade # values for filter dropdown/datalist (highest first). */
export function getProgressReportTradeNumberFilterList(rows) {
  const seen = new Set();
  const list = [];
  for (const row of rows || []) {
    const v = String(row.baseTradeNumber ?? "").trim();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    list.push(v);
  }
  list.sort(compareBaseTradeNumberDesc);
  return list;
}

export function applyProgressReportFilters(rows, filters) {
  const active = Object.entries(filters || {}).filter(
    ([, v]) => v != null && String(v).trim() !== "",
  );
  if (!active.length) return rows || [];
  return (rows || []).filter((row) =>
    active.every(([key, selected]) => {
      const cell = String(row[key] ?? "").trim() || "—";
      const q = String(selected).trim();
      if (PROGRESS_REPORT_TYPEABLE_FILTER_KEYS.has(key)) {
        const rowTn = String(row.tradeNumber ?? "").trim().toLowerCase();
        const ql = q.toLowerCase();
        return rowTn === ql;
      }
      if (PROGRESS_REPORT_PARTIAL_MATCH_FILTER_KEYS.has(key)) {
        const cellLower = cell === "—" ? "" : cell.toLowerCase();
        return cellLower.includes(q.toLowerCase());
      }
      return cell === selected;
    }),
  );
}

/** Date columns available for period (from–to) filtering. */
export const PROGRESS_REPORT_PERIOD_DATE_FIELDS = [
  { key: "paymentReceivedDate", label: "Commission Received" },
  { key: "paymentToAgentsDate", label: "Commission paid out" },
  { key: "invoiceDate", label: "Invoice Date" },
  { key: "openTRSDate", label: "Open TRS Date" },
  { key: "closedTRSDate", label: "Closed TRS Date" },
];

function parseProgressReportYmd(isoOrDisplay) {
  const iso = progressReportDateForInput(isoOrDisplay);
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return null;
  return new Date(y, m - 1, d);
}

/** Keep rows whose selected date column falls within from–to (inclusive). */
export function applyProgressReportPeriodFilter(
  rows,
  { dateField, fromDate, toDate } = {},
) {
  if (!dateField) return rows || [];
  const from = fromDate ? parseProgressReportYmd(fromDate) : null;
  const to = toDate ? parseProgressReportYmd(toDate) : null;
  if (!from && !to) return rows || [];

  return (rows || []).filter((row) => {
    const cell = parseProgressReportYmd(row[dateField]);
    if (!cell) return false;
    if (from && cell < from) return false;
    if (to && cell > to) return false;
    return true;
  });
}

export function getProgressReportPeriodDateFieldLabel(key) {
  return (
    PROGRESS_REPORT_PERIOD_DATE_FIELDS.find((f) => f.key === key)?.label || key
  );
}

export function getProgressReportColumnLabel(key) {
  const col = PROGRESS_REPORT_COLUMNS.find((c) => c.key === key);
  return col?.label || key;
}

/** Parse display date to yyyy-mm-dd for date inputs, or return "". */
export function progressReportDateForInput(value) {
  if (!value) return "";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parts = raw.split(/[/-]/);
  if (parts.length === 3) {
    let y;
    let m;
    let d;
    if (parts[0].length === 4) {
      y = parseInt(parts[0], 10);
      m = parseInt(parts[1], 10);
      d = parseInt(parts[2], 10);
    } else {
      m = parseInt(parts[0], 10);
      d = parseInt(parts[1], 10);
      y = parseInt(parts[2], 10);
      if (y < 100) y += 2000;
    }
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    }
  }
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const mo = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${mo}-${day}`;
  }
  return "";
}

export function progressReportDateFromInput(isoValue) {
  if (!isoValue) return "";
  const [y, m, d] = isoValue.split("-").map(Number);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return isoValue;
  return new Date(y, m - 1, d).toLocaleDateString("en-CA");
}
